import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FwDialogService } from '@flywheel-io/vision';
import * as Sentry from '@sentry/angular';
import { ReplaySubject, Observable, firstValueFrom } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import { CachingService } from './caching.service';
import { CoreApiService, queryAll } from './core-api.service';
import { ErrorDialog } from './dialogs/error-dialog/error-dialog.component';
import { createCachedResource } from './shared/caching.utils';
import { RequestState } from './shared/enums/request-state.enum';
import { ApiConfig, CentralConfig, DeleteReasons, ProjectLockingReasons } from './shared/models/api-config.model';
import { FileTypes } from './shared/models/file.model';
import { ProjectSharingFilterOptions } from './shared/models/project.model';
import { Provider } from './shared/models/provider.model';
import { SiteSettings } from './shared/models/site-settings.model';
import { Version } from './shared/models/version.model';
import { queryStringToParams } from './shared/url.utils';
import { isVersionAtLeast } from './shared/version.utils';
import { loadGoogleTagManager } from './vendor/gtag';
import { getError, initializeSentry } from './vendor/sentry';
import { SSE, SSEOptions } from './vendor/sse';
import { loadUserpilotScript } from './vendor/userpilot';

interface Features {
  [name: string]: boolean | string
}

/**
 * These are features that don't require explicit configuration in order to be enabled.
 */
const standardFeatures: Features = {
  'auth0': true,
  'default_viewer_apps': true,
  'google_analytics': true,
  'ohif_slice_order': 'alpha',
  'sentry_logging': false,
  'userpilot': true,
};


@Injectable({
  providedIn: 'root',
})
export class ConfigService extends CachingService {

  private http: HttpClient // for making requests that bypass configured interceptors

  constructor(
    private dialog: FwDialogService,
    private coreApi: CoreApiService,
    handler: HttpBackend,
  ) {
    super();
    this.http = new HttpClient(handler);
  }


  public apiConfig: ApiConfig
  public features: Features = {}
  public wipFeatures: Features = {}
  public authAudience: string

  private deleteReasons = {
    state: RequestState.Empty,
    subject: new ReplaySubject<DeleteReasons>(1),
    request: this.coreApi.config.deleteReasons.get(),
  }
  readonly deleteReasons$ = this.deleteReasons.subject.asObservable()

  private fileTypes = createCachedResource(this.coreApi.config.fileTypes.get())

  private projectLockingReasons = {
    state: RequestState.Empty,
    subject: new ReplaySubject<ProjectLockingReasons>(1),
    request: this.coreApi.config.projectLockingReasons.get(),
  }
  readonly projectLockingReasons$ = this.projectLockingReasons.subject.asObservable()

  private projectSharingFilterOptions = createCachedResource(this.coreApi.config.projectSharingFilterOptions.get())

  private providers = {
    state: RequestState.Empty,
    subject: new ReplaySubject<Provider[]>(1),
    request: queryAll(this.coreApi.site.providers.query),
  }
  readonly providers$ = this.providers.subject.asObservable()

  private siteSettings = {
    state: RequestState.Empty,
    subject: new ReplaySubject<SiteSettings>(1),
    request: this.coreApi.site.settings.get(),
  }
  readonly siteSettings$ = this.siteSettings.subject.asObservable()

  private version = {
    state: RequestState.Empty,
    subject: new ReplaySubject<Version>(1),
    request: this.coreApi.version(),
  }
  readonly version$ = this.version.subject.asObservable()

  /**
   * Load the config object from the API and initialize properties on this service.
   * This is intended to be called as an APP_INITIALIZER provider factory before
   * the Angular app is initialized.
   */
  loadApiConfig(): Promise<ApiConfig> {
    return firstValueFrom(this.http.get<ApiConfig>('/api/config')).catch(error => {
      /* eslint-disable-next-line no-console */
      console.error('Error while fetching site config ', error);
      // Sentry.captureException(getError(apiConfig.error));
      this.showErrorDialog();
      throw error;
    }).then(apiConfig => {
      this.apiConfig = apiConfig;
      // Standard Features <- API Config <- Query Parameters
      this.features = {
        ...standardFeatures,
        ...apiConfig.features,
        ...queryStringToParams(window.location.search, coerceBoolean),
        ...this.getLocalStorageFeatureOverrides(),
      }
      this.wipFeatures = Object.assign({}, apiConfig.wip_features);

      const sentryEnabled = this.isFeatureEnabled('sentry_logging');
      if (sentryEnabled) {
        initializeSentry();
      }

      if (apiConfig.error) {
        console.error('Error while fetching site config ', apiConfig.error); // eslint-disable-line no-console
        this.showErrorDialog();
        if (sentryEnabled) {
          Sentry.captureException(getError(apiConfig.error));
        }
      }

      if (this.isFeatureEnabled('google_analytics')) {
        const measurementId = this.getFeatureSetting('google_analytics');
        loadGoogleTagManager(typeof measurementId === 'string' ? measurementId : undefined);
      }
      if (this.isFeatureEnabled('userpilot')) {
        loadUserpilotScript();
      }
      return apiConfig;
    });
  }

  getLocalStorageFeatureOverrides(): Features {
    const overridesString = localStorage.getItem('flywheelFeatureOverrides');
    try {
      const parsedOverrides = JSON.parse(overridesString);
      return parsedOverrides || {};
    }
    catch (err) {
      /* eslint-disable-next-line no-console */
      console.error(`Error parsing feature overrides from local storage: ${(err as SyntaxError)?.message}`);
      return {};
    }
  }

  getApiUrl(url: string): string {
    return (this.apiConfig?.api_base ?? '') + url;
  }

  getFeatureSetting(name: string): boolean | string {
    return this.features[name];
  }

  getInProgressFeatureSetting(name: string): boolean | string {
    return this.wipFeatures[name];
  }

  isFeatureEnabled(name: string): boolean {
    return Boolean(this.getFeatureSetting(name));
  }

  isInProgressFeatureEnabled(name: string): boolean {
    return Boolean(this.getInProgressFeatureSetting(name));
  }

  getEnabledFeatures(): string[] {
    return Object.entries(this.features).filter(([, value]) => Boolean(value)).map(([key]) => key);
  }

  getCentralConfig(): CentralConfig {
    return this.apiConfig.central;
  }

  getDeleteReasons({ refresh = false } = {}): Observable<DeleteReasons> {
    return this.getCacheableResource(this.deleteReasons, this.deleteReasons$, { refresh });
  }

  getProjectLockingReasons({ refresh = false } = {}): Observable<ProjectLockingReasons> {
    return this.getCacheableResource(this.projectLockingReasons, this.projectLockingReasons$, { refresh });
  }

  getProjectSharingFilterOptions({ refresh = false } = {}): Observable<ProjectSharingFilterOptions> {
    if (refresh) {
      this.projectSharingFilterOptions.refresh$.next();
    }
    return this.projectSharingFilterOptions.result$;
  }

  getProviders({ refresh = false } = {}): Observable<Provider[]> {
    return this.getCacheableResource(this.providers, this.providers$, { refresh });
  }

  getSiteSettings({ refresh = false } = {}): Observable<SiteSettings> {
    return this.getCacheableResource(this.siteSettings, this.siteSettings$, { refresh });
  }

  getVersion({ refresh = false } = {}): Observable<Version> {
    return this.getCacheableResource(this.version, this.version$, { refresh });
  }

  getFileTypes({ refresh = false } = {}): Observable<FileTypes> {
    if (refresh) {
      this.fileTypes.refresh$.next();
    }
    return this.fileTypes.result$;
  }

  setSiteSettings(body: Partial<SiteSettings>): Observable<SiteSettings> {
    const observable = this.coreApi.site.settings.put(body).pipe(
      tap((updatedSiteSettings) => this.siteSettings.subject.next(updatedSiteSettings)),
      shareReplay(1)
    );
    return observable;
  }

  /**
   * Use to check if flywheel_release version is at least the targetVersion or greater.
   * @param {string} targetVersion - version to check against i.e. '15.6.0'
   * @returns {Observable<boolean>} - assumes true if flywheel_release cannot be parsed; otherwise true if version is >= targetVersion
   */
  isVersionAtLeast(targetVersion: string): Observable<boolean> {
    return this.getVersion().pipe(map(version => isVersionAtLeast(version.flywheel_release, targetVersion)));
  }

  createSSE(url: string, options: SSEOptions): SSE {
    return new SSE(url, options);
  }

  /**
   * Ensure `loadApiConfig` is called before calling this!
   */
  isAuth0Enabled(): boolean {
    if (this.features.auth0 === false || this.features.auth0 === 'false') {
      return false;
    }
    if (!this.apiConfig.auth.auth0) {
      return false;
    }
    if (typeof this.apiConfig.features.auth0 === 'boolean') {
      return Boolean(this.apiConfig.features.auth0);
    }
    return true;
  }

  private showErrorDialog(): void {
    this.dialog.open(ErrorDialog, {
      data: {
        contentText: 'Configuration could not be retrieved. Reload the page or contact an administrator if the problem persists.',
        title: 'Configuration Error',
        cancelButtonText: 'Close',
      },
    });
  }
}

function coerceBoolean(value = ''): boolean | string {
  switch (value.toLowerCase()) {
    case 'true':
      return true;
    case 'false':
      return false;
    default:
      return value;
  }
}
